import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../auth/services/auth.service';
import { InfoBannerComponent } from '../../../../shared/components/ui/info-banner/info-banner.component';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import {
  formatDisclosurePartnerRef,
  resolveDisclosureScopeLabel,
} from '../../helpers/disclosure-scope.helper';
import { OAuthConsentParams } from '../../models/oauth-consent.model';
import { OAuthConsentService } from '../../services/oauth-consent.service';
import { getValvurApiError } from '../../../../core/errors/parse-valvur-api-error';
import { SubdomainService } from '../../../../core/services/subdomain.service';

const OAUTH_RETURN_STORAGE_KEY = 'valvur_oauth_return';

@Component({
  selector: 'app-oauth-consent',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    InfoBannerComponent,
    ParentButtonComponent,
  ],
  templateUrl: './oauth-consent.component.html',
  styleUrl: './oauth-consent.component.scss',
})
export class OAuthConsentComponent implements OnInit {
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #auth = inject(AuthService);
  #consent = inject(OAuthConsentService);
  #translate = inject(TranslateService);
  #subdomain = inject(SubdomainService);

  readonly loading = signal(true);
  readonly confirming = signal(false);
  readonly pageError = signal<string | null>(null);
  readonly clientName = signal('');
  readonly clientId = signal('');
  readonly environment = signal('');
  readonly scopeIds = signal<string[]>([]);
  readonly canAuthorize = signal(false);

  #params: OAuthConsentParams | null = null;

  ngOnInit(): void {
    void this.bootstrap();
  }

  scopeLabel(scopeId: string): string {
    return resolveDisclosureScopeLabel(scopeId, (key) =>
      this.#translate.instant(key),
    );
  }

  partnerLabel(): string {
    const name = this.clientName().trim();
    if (name) {
      return name;
    }
    return formatDisclosurePartnerRef(this.clientId());
  }

  async continueToPartner(): Promise<void> {
    if (!this.#params || !this.canAuthorize() || this.confirming()) {
      return;
    }

    this.confirming.set(true);
    this.pageError.set(null);

    try {
      const result = await this.#consent.confirm(this.#params);
      const redirectUrl = result.redirectUrl?.trim();
      if (!redirectUrl) {
        this.pageError.set(
          this.#translate.instant('app.identity.oauthConsent.errors.confirm'),
        );
        return;
      }
      window.location.assign(redirectUrl);
    } catch (err: unknown) {
      const api = getValvurApiError(err);
      this.pageError.set(
        api?.message ??
          this.#translate.instant('app.identity.oauthConsent.errors.confirm'),
      );
    } finally {
      this.confirming.set(false);
    }
  }

  cancel(): void {
    if (this.#params?.redirectUri) {
      try {
        const url = new URL(this.#params.redirectUri);
        url.searchParams.set('error', 'access_denied');
        if (this.#params.state) {
          url.searchParams.set('state', this.#params.state);
        }
        window.location.assign(url.toString());
        return;
      } catch {
        // fall through
      }
    }

    if (this.#subdomain.isOAuthSubdomain()) {
      void this.#router.navigate(['/auth']);
      return;
    }

    void this.#router.navigate(['/home']);
  }

  private async bootstrap(): Promise<void> {
    const params = this.readParams();
    if (!params) {
      this.loading.set(false);
      this.pageError.set(
        this.#translate.instant('app.identity.oauthConsent.errors.invalidRequest'),
      );
      return;
    }

    this.#params = params;

    if (!this.#auth.isAuthenticated()) {
      sessionStorage.setItem(OAUTH_RETURN_STORAGE_KEY, this.#router.url);
      await this.#router.navigate(['/auth']);
      return;
    }

    await this.loadPreview(params);
  }

  private async loadPreview(params: OAuthConsentParams): Promise<void> {
    this.loading.set(true);
    this.pageError.set(null);

    try {
      const preview = await this.#consent.preview(params);
      this.clientId.set(preview.clientId?.trim() ?? params.clientId);
      this.clientName.set(preview.clientName?.trim() ?? '');
      this.environment.set(preview.environment?.trim() ?? 'sandbox');
      this.scopeIds.set(
        (preview.scopes ?? [])
          .map((scope) => scope.scopeId?.trim() ?? '')
          .filter(Boolean),
      );
      this.canAuthorize.set(preview.canAuthorize !== false);
    } catch (err: unknown) {
      const api = getValvurApiError(err);
      this.pageError.set(
        api?.message ??
          this.#translate.instant('app.identity.oauthConsent.errors.preview'),
      );
    } finally {
      this.loading.set(false);
    }
  }

  private readParams(): OAuthConsentParams | null {
    const query = this.#route.snapshot.queryParamMap;
    const clientId = query.get('client_id')?.trim() ?? '';
    const redirectUri = query.get('redirect_uri')?.trim() ?? '';
    const scope = query.get('scope')?.trim() ?? '';
    const codeChallenge = query.get('code_challenge')?.trim() ?? '';
    const codeChallengeMethod = query.get('code_challenge_method')?.trim();
    const state = query.get('state')?.trim();

    if (!clientId || !redirectUri || !scope || !codeChallenge) {
      return null;
    }

    return {
      clientId,
      redirectUri,
      scope,
      codeChallenge,
      codeChallengeMethod,
      state,
    };
  }
}
